import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from '../BasicElement';
import Button, { ILink } from '../Button';
import Carousel from '../Carousel';
import CarouselItem from '../CarouselItem';
import Image, { IImage } from '../Image';
import {kebabCase} from 'lodash';

export interface IVHSCarouselItems {
	/**
	 *  Item Heading
	 */
	heading: string;
	/**
	 *  Item Subheading
	 */
	subheading: string;
	/**
	 * Item Link
	 */
	link?: ILink;
	/**
	 * Item Image
	 */
	image?: IImage;
	/**
	 *  Item Text
	 */
	text: string;
}
export interface IVHSCarouselProps extends IBaseElementProps {
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Please provide a unique ID for this particular accordion. It should be lowercase with hyphens separating the words, like main-carousel or student-spotlights. Do not include spaces or any other characters. Try to keep it relatively short.
	 * @control uniqueid
	 */
	id?: string;
	/**
	 * Items
	 */
	items?: IVHSCarouselItems[];
	/**
	 * CTA
	 */
	cta?: ILink;
	/**
	 * CTA Style
	 */
	ctaStyle?:  'primary' | 'secondary';
	/**
	 * CTA Size
	 */
	ctaSize?:  'large' | 'small';
	showArrows?: boolean;
}

/**
 * Carousel
 * @block
 * @icon slides
 */
const VHSCarousel: React.FC<IVHSCarouselProps> = ({
	id,
	heading,
	items = [],
	cta,
	ctaStyle,
	ctaSize,
	showArrows = false,
	className,
	classNames = [],
}) => {

	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const handleTabChange = () => {

			const allTiles = ref.current?.querySelectorAll(
				'.glide__slide--clone a',
			) as NodeListOf<HTMLButtonElement>;

			allTiles && allTiles.forEach((tile) => {
				tile.setAttribute('tabindex', '-1');
			});

			const afterActiveTiles = ref.current?.querySelectorAll(
				'.glide__slide--active ~ .glide__slide--clone a',
			) as NodeListOf<HTMLButtonElement>;

			afterActiveTiles && afterActiveTiles.forEach((tile) => {
				tile.removeAttribute('tabindex');
			});
		};

		const document = window.document;
		document.addEventListener('DOMContentLoaded', handleTabChange);

		return () => {
			document.removeEventListener('DOMContentLoaded', handleTabChange);
		};
	}, []);

	const carouselOptions = {
		type: 'carousel',
		rewind: false,
		bound: true,
		gap: 0,
		perView: 4,
		showArrows: showArrows,
		showBullets: false,
		peek: {
			before: 0,
			after: 0,
		},
		breakpoints: {
			640: {
				perView: 1,
				peek: {
					before: 0,
					after: 0,
				},
			},
			765: {
				perView: 1,
				peek: {
					before: 0,
					after: 0,
				},
			},
			900: {
				perView: 2,
				peek: {
					before: 0,
					after: 0,
				},
			},
			1140: {
				perView: 3,
				peek: {
					before: 0,
					after: 0,
				},
			},
		},
	};
	return (
		<div className={className || cn('vhs-carousel', ...classNames)} ref={ref}>
			<div className="vhs-carousel__container">
				{heading && <h2 className="vhs-carousel__heading">{heading}</h2>}
				{items.length > 0 &&
					<div className="vhs-carousel__wrapper">
						<div>
							<Carousel
								perView={1}
								options={carouselOptions}
								id={`carousel-${id || kebabCase(heading)}`}
							>
								{items.map(( {link, image, heading, subheading, text}, index ) => (
									<CarouselItem key={index}>
										<a
											href={link?.href}
											className="vhs-carousel__item"
											target={link?.target}
											rel={link?.target === '_blank' ? 'noopener noreferrer' : 'noopener'}
										>
											<div className="vhs-carousel__image-wrapper">
												<Image
													{...image}
													className="vhs-carousel__image img-fluid"
												/>
											</div>
											<div className="vhs-carousel__content">
												{heading && <h3 className="vhs-carousel__item-heading">{heading}</h3>}
												{subheading && <h6 className="vhs-carousel__subheading">{subheading}</h6>}
												{text && <p className="vhs-carousel__text">{text}</p>}
											</div>
										</a>
									</CarouselItem>
								))}
							</Carousel>
						</div>
					</div>
				}
				{cta?.label && (
					<div className="vhs-carousel__cta-wrapper">
						<Button
							{...cta}
							className={cn(
								'vhs-carousel__cta btn btn-primary',
								(ctaStyle === 'secondary') && 'btn-secondary',
								(ctaSize === 'small') && 'btn-sm',
							)}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default VHSCarousel;
